import React, { useCallback, useEffect, useState } from "react";
import AuthService from "../service/AmplifyAuth";
import TrustdService from "../service/TrustdService";
import { Spinner } from "../components/UI";
import { useToast } from "../hooks";

const formatProvider = (listing) => {
  if (listing.source !== "pms") return "Manual";
  const provider = String(listing.provider || "PMS").trim();
  return provider ? `${provider.charAt(0).toUpperCase()}${provider.slice(1)}` : "PMS";
};

function ReadOnlyStatus({ enabled, alwaysOn = false }) {
  const isEnabled = alwaysOn || Boolean(enabled);
  return (
    <span className={`my-listings-status ${isEnabled ? "enabled" : "disabled"}`}>
      <i className={`pi ${isEnabled ? "pi-check-circle" : "pi-minus-circle"}`} aria-hidden="true" />
      {isEnabled ? "On" : "Off"}
    </span>
  );
}

export default function Listings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const addToast = useToast();

  const load = useCallback(async (email, showLoading = false) => {
    if (!email) return;
    if (showLoading) setLoading(true);
    try {
      const result = await TrustdService.getListings(email, { sync: false });
      setListings(result.listings || []);
    } catch (error) {
      addToast({
        severity: "error",
        summary: "My Listings",
        detail: error?.response?.data?.message || "Listings could not be loaded.",
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    let active = true;
    AuthService.currentAuthenticatedUserAttributes().then((attributes) => {
      if (!active) return;
      const email = String(attributes.username || "").toLowerCase();
      const cached = TrustdService.getCachedListings(email);
      if (cached?.listings) {
        setListings(cached.listings);
        setLoading(false);
      }
      void load(email, !cached);
    });
    return () => { active = false; };
  }, [load]);

  return (
    <div className="my-listings-page">
      <header className="my-listings-head">
        <div className="my-listings-head-copy">
          <span className="my-listings-head-icon" aria-hidden="true"><i className="pi pi-building" /></span>
          <div>
            <p className="dashboard-section-kicker">Portfolio</p>
            <h2>My Listings</h2>
          </div>
        </div>
        <div className="my-listings-count"><i className="pi pi-home" aria-hidden="true" /><span><strong>{listings.length}</strong>{listings.length === 1 ? "Listing" : "Listings"}</span></div>
      </header>

      <section className="my-listings-table-card" aria-label="My listings">
        <div className="my-listings-table-head">
          <div><h3>Property Overview</h3><p>Your listing information and current screening requirements.</p></div>
        </div>
        {loading && listings.length === 0 ? (
          <Spinner />
        ) : listings.length === 0 ? (
          <div className="my-listings-empty">
            <i className="pi pi-building" aria-hidden="true" />
            <h3>No listings yet</h3>
            <p>Listings added from Account will appear here.</p>
          </div>
        ) : (
          <div className="my-listings-table-scroll">
            <table className="my-listings-table">
              <thead>
                <tr>
                  <th>Listing</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Background Screening</th>
                  <th>ID Verification</th>
                  <th>Selfie Verification</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((listing) => (
                  <tr key={listing.id}>
                    <td>
                      <span className={`my-listings-property-icon ${listing.source}`}><i className={`pi ${listing.source === "pms" ? "pi-cloud" : "pi-home"}`} /></span>
                      <span className="my-listings-property-copy"><strong>{listing.name}</strong></span>
                    </td>
                    <td><span className={`listing-source ${listing.source}`}>{formatProvider(listing)}</span></td>
                    <td><span className={`my-listings-active-state ${listing.isActive ? "active" : "inactive"}`}>{listing.isActive ? "Active" : "Inactive"}</span></td>
                    <td><ReadOnlyStatus alwaysOn /></td>
                    <td><ReadOnlyStatus enabled={listing.isIdVerificationEnabled} /></td>
                    <td><ReadOnlyStatus enabled={listing.isSelfieVerificationEnabled} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
